import React, { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "../../components/Layout";
import MemList from "../memreq/MemList";
import { addRequest } from "../../store/slices/memberSlice";
import { createAdjustmentRequest } from "../../services";
import { requestTypes } from "../../utils/Helpers";
import { Link } from "react-router-dom";
import {
  Users,
  Calendar as CalendarIcon,
  Clock,
  ClipboardList,
  Send,
  ChevronDown,
  Type,
  AlignLeft,
  CalendarDays,
  LayoutList
} from "lucide-react";
import DateBox from "../../components/DateBox";
import TimeBox from "../../components/TimeBox";
import CustomSelect from "../../components/CustomSelect";
import moment from "moment";

export default function MemReq() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateRange, setDateRange] = useState([null, null]);
  const [isLoading, setIsLoading] = useState(false);
  const [RequestType, setRequestType] = useState("");
  const [StartTime, setStartTime] = useState(null);
  const [EndTime, setEndTime] = useState(null);
  const [RequestDetail, setRequestDetail] = useState("");
  const [errMsg, setErrMsg] = useState({
    err: false,
    msg: "",
    color: "warning",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const dispatch = useDispatch();
  const userProfile = useSelector((state) => state.user.userinfo);

  const notify = (isSuccess, message) => {
    if (isSuccess) {
      toast.success(message, {
        style: { className: "bg-white", color: "black" },
        position: "top-right",
      });
    } else {
      toast.error(message, {
        style: { className: "bg-white", color: "red" },
        position: "top-right",
      });
    }
  };

  function mergeDateTime(date, time) {
    const dateTime = new Date(date);
    const timeParts = new Date(time);

    dateTime.setHours(timeParts.getHours());
    dateTime.setMinutes(timeParts.getMinutes());
    dateTime.setSeconds(timeParts.getSeconds());

    return dateTime.toISOString();
  }

  const validateForm = () => {
    if (!RequestType || RequestType === "Select Type") {
      return "Please select a request type.";
    }

    switch (requestTypes[RequestType]) {
      case "Leave":
        if (!dateRange[0] || !dateRange[1]) {
          return "Please select both start and end dates.";
        }
        break;
      case "Long Break":
        if (!StartTime || !EndTime || !selectedDate) {
          return "Please select both start and end times.";
        }
        break;
      case "Overtime":
        if (!StartTime || !EndTime || !selectedDate) {
          return "Please select both start and end times and the date.";
        }
        break;
      case "Breaks Adjustment":
        if (!selectedDate) {
          return "Please select the date for Breaks Adjustment.";
        }
        break;
      case "CheckIn Adjustment":
        if (!selectedDate || !StartTime) {
          return "Please select the CheckIn date and time.";
        }
        break;
      case "CheckOut Adjustment":
        if (!selectedDate || !StartTime) {
          return "Please select the CheckOut date and time.";
        }
        break;
      case "Half Day":
        if (!StartTime || !EndTime || !selectedDate) {
          return "Please select the Half Day time.";
        }
        break;
      default:
        return "Please select a request type.";
    }

    if (!RequestDetail || RequestDetail.trim() === "") {
      return "Please fill in all fields.";
    }
    return null;
  };

  const HandleRangeAPi = () => {
    switch (requestTypes[RequestType]) {
      case "Leave":
        return {
          RequestType: Number(RequestType),
          StartDate: dateRange[0],
          EndDate: dateRange[1],
          RequestDetail,
        };

      case "Long Break":
        return {
          RequestType: Number(RequestType),
          StartDate: mergeDateTime(selectedDate, StartTime),
          EndDate: mergeDateTime(selectedDate, EndTime),
          RequestDetail,
        };
      case "CheckIn Adjustment":
        return {
          RequestType: Number(RequestType),
          StartDate: mergeDateTime(selectedDate, StartTime),
          EndDate: null,
          RequestDetail,
        };
      case "CheckOut Adjustment":
        return {
          RequestType: Number(RequestType),
          StartDate: mergeDateTime(selectedDate, StartTime),
          EndDate: null,
          RequestDetail,
        };

      case "Overtime":
        return {
          RequestType: Number(RequestType),
          StartDate: mergeDateTime(selectedDate, StartTime),
          EndDate: mergeDateTime(selectedDate, EndTime),
          RequestDetail,
        };

      case "Breaks Adjustment":
        return {
          RequestType: Number(RequestType),
          StartDate: mergeDateTime(selectedDate, StartTime),
          EndDate: mergeDateTime(selectedDate, EndTime),
          RequestDetail,
        };
      case "Half Day":
        return {
          RequestType: Number(RequestType),
          StartDate: mergeDateTime(selectedDate, StartTime),
          EndDate: mergeDateTime(selectedDate, EndTime),
          RequestDetail,
        };

      case "Select Type":
      case "":
        return "Please select a request type.";
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      notify(false, validationError);
      setErrMsg({ err: true, msg: validationError, color: "red" });
      return;
    }
    setIsLoading(true);
    setErrMsg("");

    try {
      const requestData = HandleRangeAPi();
      const requestapi = await createAdjustmentRequest(requestData);

      if (requestapi.status === 200) {
        dispatch(addRequest(requestapi?.data?.Result));
        setRequestDetail("");
        setStartTime(null);
        setEndTime(null);
        setDateRange([null, null]);
        setRequestType("Select Type");
        setSelectedDate(null);
        notify(true, "Request Submitted Successfully!");
      } else {
        setErrMsg("Error submitting request. Please try again.");
        notify(false, "Error submitting request. Please try again.");
      }
    } catch (error) {
      setErrMsg("Submission failed. Please try again.");
      console.error("Error:", error);
      notify(false, "Submission failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestTypeChange = (event) => {
    const value = event.target.value;
    setRequestType(value);
  };

  const formatTime = (date) => {
    if (!date) return "--:--";
    const time = new Date(date);
    return moment(time).format("h:mm A");
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toISOString().split("T")[0];
  };

  return (
    <Layout>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="px-4 py-4 max-w-7xl mx-auto"
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-500/10 rounded-xl">
              <ClipboardList className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Member Request</h2>
              <p className="text-gray-500 dark:text-gray-400 text-sm">Submit and manage your adjustment requests</p>
            </div>
          </div>

          {userProfile.MemberRole == 2 && (
            <Link
              to="/allrequests"
              className="flex items-center gap-2 px-5 py-2.5 bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400 font-bold rounded-xl border border-blue-100 dark:border-blue-500/20 hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all active:scale-95 shadow-sm hover:shadow-blue-500/10"
            >
              <LayoutList className="w-5 h-5" />
              View All Requests
            </Link>
          )}
        </div>

        <div className="bg-white dark:bg-gray-900/50 backdrop-blur-xl border border-gray-100 dark:border-blue-gray-900 p-8 pb-6 rounded-2xl shadow-xl shadow-gray-200/50 dark:shadow-none mb-4">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-end">
              {/* Request Type */}
              <div className="space-y-2">
                <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                  <Type className="w-4 h-4 text-gray-400" />
                  Request Type
                </label>
                <CustomSelect
                  options={requestTypes}
                  value={RequestType}
                  onChange={(e) => setRequestType(e.target.value)}
                  placeholder="Select Request Type"
                  disabled={isLoading}
                />
              </div>
              {/* Conditional Inputs */}
              {(requestTypes[RequestType] === "Leave" ||
                requestTypes[RequestType] === "Long Break" ||
                requestTypes[RequestType] === "Overtime" ||
                requestTypes[RequestType] === "Breaks Adjustment" ||
                requestTypes[RequestType] === "CheckIn Adjustment" ||
                requestTypes[RequestType] === "CheckOut Adjustment" ||
                requestTypes[RequestType] === "Half Day") && (
                  <>
                    {/* Date Selection */}
                    <div className="space-y-2">
                      <label className="block text-sm  text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                        <CalendarDays className="w-4 h-4 text-gray-400" />
                        {requestTypes[RequestType] === "Leave" ? "Leave Range" : "Select Date"}
                      </label>
                      <div className="relative group">
                        <button
                          type="button"
                          onClick={() => setShowDatePicker(true)}
                          className="w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none"
                        >
                          <div className="flex items-center gap-2 flex-1 overflow-hidden">
                            {requestTypes[RequestType] === "Leave" ? (
                              <>
                                <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 text-center truncate">
                                  {dateRange[0] ? moment(dateRange[0]).format("DD MMM YYYY") : "Start Date"}
                                </div>
                                <span className="text-gray-400 dark:text-gray-500 font-medium text-[13px] px-1 shrink-0">To</span>
                                <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 text-center truncate">
                                  {dateRange[1] ? moment(dateRange[1]).format("DD MMM YYYY") : "End Date"}
                                </div>
                              </>
                            ) : (
                              <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-medium tracking-wide shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 text-center">
                                {selectedDate ? moment(selectedDate).format("DD MMM YYYY") : "Select Date"}
                              </div>
                            )}
                          </div>
                          <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showDatePicker ? 'rotate-180 text-blue-500' : ''}`} />
                          </div>
                        </button>

                        {showDatePicker && (
                          <DateBox
                            mode={requestTypes[RequestType] === "Leave" ? "range" : "single"}
                            initialStartDate={requestTypes[RequestType] === "Leave" ? dateRange[0] : selectedDate}
                            initialEndDate={requestTypes[RequestType] === "Leave" ? dateRange[1] : null}
                            minDate={requestTypes[RequestType] === "Leave" ? new Date() : undefined}
                            maxDate={requestTypes[RequestType] === "Leave" ? null : new Date()}
                            onApply={(dates) => {
                              if (requestTypes[RequestType] === "Leave") {
                                setDateRange(dates);
                              } else {
                                setSelectedDate(dates);
                              }
                              setShowDatePicker(false);
                            }}
                            onClose={() => setShowDatePicker(false)}
                          />
                        )}
                      </div>
                    </div>

                    {/* Time Selection (if applicable) */}
                    {(requestTypes[RequestType] === "Long Break" ||
                      requestTypes[RequestType] === "Overtime" ||
                      requestTypes[RequestType] === "Half Day" ||
                      requestTypes[RequestType] === "CheckIn Adjustment" ||
                      requestTypes[RequestType] === "CheckOut Adjustment") && (
                        <div className="flex gap-4">
                          <div className="flex-1 space-y-2">
                            <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                              <Clock className="w-4 h-4 text-gray-400" />
                              {requestTypes[RequestType].includes("Adjustment") ? "Time" : "Start Time"}
                            </label>
                            <div className="relative group">
                              <button
                                type="button"
                                onClick={() => setShowStartTimePicker(true)}
                                className={`w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none ${showStartTimePicker ? 'ring-2 ring-blue-500/20 border-blue-500' : ''}`}
                                disabled={isLoading}
                              >
                                <div className="flex items-center gap-2 flex-1">
                                  <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-bold tracking-wider shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 text-center font-medium">
                                    {formatTime(StartTime)}
                                  </div>
                                </div>
                                <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                                  <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showStartTimePicker ? 'rotate-180 text-blue-500' : ''}`} />
                                </div>
                              </button>

                              <AnimatePresence>
                                {showStartTimePicker && (
                                  <TimeBox
                                    initialTime={StartTime}
                                    onApply={(time, shouldClose = true) => {
                                      setStartTime(time);
                                      if (shouldClose) setShowStartTimePicker(false);
                                    }}
                                    onClose={() => setShowStartTimePicker(false)}
                                  />
                                )}
                              </AnimatePresence>
                            </div>
                          </div>

                          {!(requestTypes[RequestType].includes("Adjustment")) && (
                            <div className="flex-1 space-y-2">
                              <label className="block text-sm text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-400" />
                                End Time
                              </label>
                              <div className="relative group">
                                <button
                                  type="button"
                                  onClick={() => setShowEndTimePicker(true)}
                                  className={`w-full flex items-center justify-between p-1.5 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500/20 outline-none transition-all cursor-pointer hover:bg-gray-100 dark:hover:bg-white/10 group shadow-sm dark:shadow-none ${showEndTimePicker ? 'ring-2 ring-blue-500/20 border-blue-500' : ''}`}
                                  disabled={isLoading}
                                >
                                  <div className="flex items-center gap-2 flex-1">
                                    <div className="bg-white dark:bg-white/10 px-4 py-2 rounded-lg font-bold tracking-wider shadow-sm dark:shadow-none border border-gray-100 dark:border-transparent flex-1 text-center font-medium">
                                      {formatTime(EndTime)}
                                    </div>
                                  </div>
                                  <div className="px-3 border-l border-gray-100 dark:border-white/10 ml-2">
                                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${showEndTimePicker ? 'rotate-180 text-blue-500' : ''}`} />
                                  </div>
                                </button>

                                <AnimatePresence>
                                  {showEndTimePicker && (
                                    <TimeBox
                                      initialTime={EndTime}
                                      onApply={(time, shouldClose = true) => {
                                        setEndTime(time);
                                        if (shouldClose) setShowEndTimePicker(false);
                                      }}
                                      onClose={() => setShowEndTimePicker(false)}
                                    />
                                  )}
                                </AnimatePresence>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                  </>
                )}
            </div>

            {/* Request Detail */}
            <div className="space-y-2">
              <label className="block text-sm  text-gray-700 dark:text-gray-300 ms-1 flex items-center gap-2">
                <AlignLeft className="w-4 h-4 text-gray-400" />
                Request Detail
              </label>
              <textarea
                id="RequestDetail"
                value={RequestDetail}
                onChange={(e) => setRequestDetail(e.target.value)}
                rows="5"
                className="w-full text-sm p-4 bg-gray-50 dark:bg-black/50 border border-gray-100 dark:border-blue-gray-900 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/20 outline-none transition-all duration-200 resize-none placeholder:text-gray-400 dark:placeholder:text-gray-600 shadow-sm dark:shadow-none"
                placeholder="Please describe your request in detail..."
                required
                disabled={isLoading}
              />
              <AnimatePresence>
                {errMsg.err && (
                  <motion.p
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -10 }}
                    className="text-red-500 text-xs mt-2 font-medium flex items-center gap-1.5 ms-1"
                  >
                    <span className="w-1 h-1 bg-red-500 rounded-full" />
                    {errMsg.msg}
                  </motion.p>
                )}
              </AnimatePresence>
            </div>

            <div className="flex justify-center pt-2">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="button"
                className={`group relative flex items-center justify-center gap-2 px-12 py-3.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl shadow-lg shadow-blue-500/20 transition-all disabled:opacity-70 disabled:cursor-not-allowed overflow-hidden ${isLoading ? "pl-14" : ""
                  }`}
                onClick={handleSubmit}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="absolute left-6 h-5 w-5 animate-spin rounded-full border-2 border-white/20 border-t-white" />
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <span>Submit Request</span>
                    <Send className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </>
                )}
              </motion.button>
            </div>
          </div>
        </div>


      </motion.div>
      <MemList />
    </Layout >
  );
}